import { useEffect, useRef } from 'react'
import { trackClient } from './client.ts'
import { type CommonProps, type EventName } from './constants.ts'

/**
 * Fire a "the visitor actually saw this" event once per distinct `key`, not once
 * per render.
 *
 * `key` is the thing being viewed (a scanned domain, a report token). The free
 * checker uses Post/Redirect/Get, so the component stays mounted while the
 * subject changes — keying on the subject is what makes the second scan in a
 * session count, and a re-render or a fetcher update not.
 *
 * A mount effect is the only honest place a view event can fire, which is why
 * this is one of the few sanctioned useEffects in the app. Pass `null` to skip
 * (nothing to view yet).
 */
export function useTrackOnce(
	eventName: EventName,
	key: string | null | undefined,
	properties?: CommonProps & Record<string, unknown>,
) {
	// Every subject already tracked, not just the most recent one: navigating
	// A → B → A is one view of A, and remembering only the previous key would
	// count it twice.
	const tracked = useRef<Set<string>>(new Set())
	// Held in a ref so changing property values never re-fire the event; the key
	// alone decides when a view is a new view.
	const latestProperties = useRef(properties)
	latestProperties.current = properties

	useEffect(() => {
		if (!key) return
		const subject = `${eventName}|${key}`
		if (tracked.current.has(subject)) return
		tracked.current.add(subject)
		// Safe to mark before the capture lands: trackClient buffers events sent
		// before PostHog finishes initializing and replays them on init, so an
		// early mount effect is delayed, never dropped.
		trackClient(eventName, latestProperties.current)
	}, [eventName, key])
}
